/**
 * Month helpers for the endless calendar.
 *
 * The calendar is a list of month sections. A section renders every week that
 * touches its month, and blanks out the days that belong to a neighbour — so
 * a month heading always sits directly above that month's own days, and a
 * straddling week appears in both sections with complementary gaps.
 *
 * A "month key" is `YYYY-MM`; string comparison on it is chronological.
 */

import { DAYS_IN_WEEK, addDays, startOfWeek, toDateKey } from './date.js';

export const MONTH_FULL_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** How many months are added each time the scroller reaches an edge. */
export const MONTHS_PER_EXTENSION = 2;
/** Jumping further than this rebuilds the list instead of bridging to it. */
export const MAX_BRIDGE_MONTHS = 9;

/**
 * @param {Date} date
 * @returns {string} `YYYY-MM`
 */
export function monthKeyOf(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * @param {string} monthKey
 * @returns {Date} first day of that month
 */
export function parseMonthKey(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(y, m - 1, 1);
}

/**
 * Shift a month key by a number of months.
 * @param {string} monthKey
 * @param {number} amount
 * @returns {string}
 */
export function addMonths(monthKey, amount) {
  const date = parseMonthKey(monthKey);
  return monthKeyOf(new Date(date.getFullYear(), date.getMonth() + amount, 1));
}

/**
 * Does this date fall inside the month?
 * @param {string} dateKey `YYYY-MM-DD`
 * @param {string} monthKey `YYYY-MM`
 * @returns {boolean}
 */
export function isInMonth(dateKey, monthKey) {
  return dateKey.slice(0, 7) === monthKey;
}

/**
 * Last day of a month, as a date key.
 * @param {string} monthKey
 * @returns {string}
 */
export function lastDayOfMonth(monthKey) {
  const date = parseMonthKey(monthKey);
  return toDateKey(new Date(date.getFullYear(), date.getMonth() + 1, 0));
}

/**
 * Every week that touches the month, as Mondays. The first and last of these
 * usually reach into the neighbouring months — those days render as gaps.
 * @param {string} monthKey
 * @returns {Date[]}
 */
export function weeksForMonth(monthKey) {
  const first = parseMonthKey(monthKey);
  const lastWeekStart = toDateKey(startOfWeek(new Date(first.getFullYear(), first.getMonth() + 1, 0)));

  const weeks = [];
  let cursor = startOfWeek(first);
  while (toDateKey(cursor) <= lastWeekStart) {
    weeks.push(cursor);
    cursor = addDays(cursor, DAYS_IN_WEEK);
  }
  return weeks;
}

/**
 * A contiguous run of month keys, inclusive at both ends.
 * @param {string} fromKey
 * @param {string} toKey
 * @returns {string[]}
 */
export function monthRange(fromKey, toKey) {
  if (fromKey > toKey) return [];
  const months = [];
  let cursor = fromKey;
  while (cursor <= toKey) {
    months.push(cursor);
    cursor = addMonths(cursor, 1);
  }
  return months;
}

/**
 * The default list: the month plus one either side.
 * @param {string} monthKey
 * @param {number} [before]
 * @param {number} [after]
 * @returns {string[]}
 */
export function monthsAround(monthKey, before = 1, after = 1) {
  return monthRange(addMonths(monthKey, -before), addMonths(monthKey, after));
}

/**
 * Grow the month list at one end. Returns a new array.
 * @param {string[]} months
 * @param {'start'|'end'} edge
 * @param {number} [count]
 * @returns {string[]}
 */
export function extendMonths(months, edge, count = MONTHS_PER_EXTENSION) {
  if (months.length === 0) return months;

  if (edge === 'start') {
    const first = months[0];
    return [...monthRange(addMonths(first, -count), addMonths(first, -1)), ...months];
  }

  const last = months[months.length - 1];
  return [...months, ...monthRange(addMonths(last, 1), addMonths(last, count))];
}

/**
 * Make sure a month is in the list before scrolling to it.
 *
 * Nearby months are bridged so the scroll stays continuous. A distant jump
 * would otherwise render years of empty calendar, so past `MAX_BRIDGE_MONTHS`
 * the list is simply rebuilt around the target.
 *
 * @param {string[]} months
 * @param {string} monthKey
 * @returns {string[]}
 */
export function ensureMonthInList(months, monthKey) {
  if (months.includes(monthKey)) return months;
  if (months.length === 0) return monthsAround(monthKey);

  const first = months[0];
  const last = months[months.length - 1];
  const bridged =
    monthKey < first
      ? monthRange(addMonths(monthKey, -1), last)
      : monthRange(first, addMonths(monthKey, 1));

  return bridged.length > MAX_BRIDGE_MONTHS ? monthsAround(monthKey) : bridged;
}

/**
 * First and last day covered by a list of months — the range that habit
 * occurrences need to be computed for.
 * @param {string[]} months
 * @returns {{startKey: string, endKey: string}}
 */
export function monthsDayRange(months) {
  return {
    startKey: `${months[0]}-01`,
    endKey: lastDayOfMonth(months[months.length - 1]),
  };
}

/**
 * Section heading, e.g. "August 2026".
 * @param {string} monthKey
 * @returns {string}
 */
export function formatMonthLabel(monthKey) {
  const date = parseMonthKey(monthKey);
  return `${MONTH_FULL_LABELS[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * Human distance between two months, for the handwritten sub-label.
 * @param {string} monthKey
 * @param {string} currentMonthKey
 * @returns {string}
 */
export function monthDistanceLabel(monthKey, currentMonthKey) {
  const a = parseMonthKey(currentMonthKey);
  const b = parseMonthKey(monthKey);
  const diff = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (diff === 0) return 'this month';
  if (diff === -1) return 'last month';
  if (diff === 1) return 'next month';
  return diff < 0 ? `${Math.abs(diff)} months ago` : `in ${diff} months`;
}
